import guidesMenu from "app/assets/files/guides-menu.json";
import InternalMenu from "app/components/composites/InternalMenu";
import NextPrevGroup from "app/components/composites/NextPrevGroup";
import {
	EstateSettlementsGuidesTitles,
	FuneralMattersGuidesTitles,
	getMultiStepGuideHref,
	IAllMultipageGuideListType,
	ITopic,
	MultipageGuideList,
	PalliativeCareGuidesTitles,
	PlanningAheadGuidesList,
	ReportingADeathGuidesList,
} from "app/components/templates/Guide/__common__/Topic";
import { ILinkItem } from "app/utils/interfaces";
import { useGlobalLanguageState } from "app/utils/internationalization/GlobalLanguageProvider";
import { ITranslatedObject } from "app/utils/internationalization/TranslationPageRenderer";
import { lowercaseAndHyphen, lowercaseAndHyphenSpecialChar } from "app/utils/strings";
import { ReactNode } from "react";
import "./MultiPageGuide.scss";
import SinglePageGuide from "./SinglePageGuide";

interface IProps {
	currentTopic: ITopic;
	currentGuideTitle: IAllMultipageGuideListType;
	currentTitle?: string;
	currentStep: number;
	relatedGuides:
		| typeof PlanningAheadGuidesList
		| typeof PalliativeCareGuidesTitles
		| typeof ReportingADeathGuidesList
		| typeof FuneralMattersGuidesTitles
		| typeof EstateSettlementsGuidesTitles;
	translatedObjects?: ITranslatedObject[];
	children?: ReactNode;
}

const MultiPageGuide = (props: IProps): JSX.Element => {
	const [language, selectedLanguage] = useGlobalLanguageState();
	let guideStepsTitles = MultipageGuideList[props.currentGuideTitle];

	if (props.currentGuideTitle === PlanningAheadGuidesList.advanceCarePlan) {
		guideStepsTitles = [
			"About Advance Care Planning (ACP)",
			"Steps to Advance Care Planning (ACP)",
			"Ways to prepare an Advance Care Plan (ACP)",
			"Speak to someone",
			"Choose your Nominated Healthcare Spokesperson (NHS)",
		];
	}

	/**
	 * Lookup for the translated guide step title using given guideStepTitle and currentGuideTitle
	 */
	const getTranslatedCopy = (sheetName: string, copyForTranslation: string): string => {
		const sheetData = guidesMenu[lowercaseAndHyphenSpecialChar(sheetName)];
		if (!sheetData) {
			return copyForTranslation;
		}
		const translatedCopy = sheetData[selectedLanguage].find(
			({ key }: { key: string }) => key === lowercaseAndHyphen(copyForTranslation),
		);
		return translatedCopy ? translatedCopy.data : copyForTranslation;
	};

	const linksObjects: ILinkItem[] = guideStepsTitles.map((guideStepTitle: string, index: number): ILinkItem => {
		return {
			name: getTranslatedCopy(props.currentGuideTitle, guideStepTitle),
			path: getMultiStepGuideHref(props.currentTopic.type, props.currentGuideTitle, index),
		};
	});

	const NextPrevNavigation = (): JSX.Element => {
		return (
			<div className="multi-page-guide__next-prev-grp">
				<NextPrevGroup
					currentLinkIndex={props.currentStep}
					links={linksObjects}
					nextButtonLabel={getTranslatedCopy("miscellaneous", "next")}
					previousButtonLabel={getTranslatedCopy("miscellaneous", "previous")}
					language={language}
				/>
			</div>
		);
	};

	const currentSectionTitle = getTranslatedCopy(props.currentGuideTitle, guideStepsTitles[props.currentStep]);

	return (
		<div className="multi-page-guide">
			<SinglePageGuide
				currentTopic={props.currentTopic}
				currentGuideTitle={props.currentGuideTitle}
				currentTitle={props.currentTitle}
				currentSectionTitle={props.currentStep === 0 ? "" : currentSectionTitle} // for the 1st/overview section title is empty, it should show guide title instead
				relatedGuides={props.relatedGuides}
				internalMenu={
					<InternalMenu.Guides
						currentActiveLink={currentSectionTitle}
						links={linksObjects}
						accordionTitle={getTranslatedCopy("miscellaneous", "contents")}
					/>
				}
				translatedObjects={props.translatedObjects}
			>
				{props.children}
				<NextPrevNavigation />
			</SinglePageGuide>
		</div>
	);
};

export default MultiPageGuide;
